import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Search, Navigation, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { loadGoogleMapsPlaces, extractPincode } from "../../utils/googleMapsLoader";

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const PICKED_ZOOM = 16;

/**
 * BookingLocationPicker — Google Maps address picker for the customer
 * booking flow. On place-select / map-click / GPS, it fills the parent's
 * `address` + `pincode` + `location_lat` + `location_lng` fields.
 *
 * Design notes:
 *   - The init effect runs EXACTLY ONCE. Previously, `onLocationPicked`
 *     was passed inline from the parent, so its identity changed every
 *     render and the effect re-ran — tearing down the map mid-interaction.
 *     We now proxy the callback through a ref so the effect stays stable
 *     while still invoking the latest parent handler.
 *   - Pin + "Location pinned" chip appear even when reverse-geocoding fails
 *     (common when the GCP key lacks Geocoding API), so customers always
 *     see immediate feedback.
 */
export const BookingLocationPicker = ({ onLocationPicked }) => {
  const mapRef = useRef(null);
  const searchRef = useRef(null);
  const mapInst = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const placesSvcRef = useRef(null);

  // Keep the latest parent callback accessible without re-firing the init
  // effect each render.
  const onPickedRef = useRef(onLocationPicked);
  useEffect(() => { onPickedRef.current = onLocationPicked; }, [onLocationPicked]);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [picked, setPicked] = useState(null); // {address, pincode, lat, lng}

  // Stable emit — never changes identity, so safe to call from map listeners
  // installed during the single init effect.
  const emit = useCallback((payload) => {
    setPicked(payload);
    onPickedRef.current?.(payload);
  }, []);

  const upsertMarker = useCallback((google, pos) => {
    const map = mapInst.current;
    if (!map) return;
    // StrictMode / HMR / parent-rerender can leave markerRef pointing at a
    // marker whose parent map was replaced. Detect the stale pair and
    // recreate so the marker always lands on the live map.
    const stale = markerRef.current && markerRef.current.getMap?.() !== map;
    if (stale) {
      try { markerRef.current.setMap(null); } catch { /* ignore */ }
      markerRef.current = null;
    }
    if (!markerRef.current) {
      markerRef.current = new google.maps.Marker({ map, position: pos, draggable: true });
      markerRef.current.addListener("dragend", () => {
        const p = markerRef.current.getPosition();
        reverseAndEmit(p.lat(), p.lng());
      });
    } else {
      markerRef.current.setPosition(pos);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reverse-geocode a lat/lng. We ALWAYS emit at least the coords so the
  // parent form gets a precise point even when the geocoder is unavailable.
  const reverseAndEmit = useCallback((lat, lng) => {
    const baseline = { address: "", pincode: "", lat, lng };
    if (!geocoderRef.current) { emit(baseline); return; }
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const r = results[0];
        emit({
          address: r.formatted_address || "",
          pincode: extractPincode(r),
          lat,
          lng,
        });
      } else {
        emit(baseline);
      }
    });
  }, [emit]);

  // Move the map to a typed query. Works even when the Places Autocomplete
  // dropdown isn't used/visible: we geocode the text, and fall back to Places
  // Text Search if forward-geocoding is unavailable on the key.
  const moveTo = useCallback((lat, lng, address, pincode) => {
    const google = window.google;
    const map = mapInst.current;
    if (map && google?.maps) {
      upsertMarker(google, { lat, lng });
      map.panTo({ lat, lng });
      map.setZoom(PICKED_ZOOM);
    }
    emit({ address: address || "", pincode: pincode || "", lat, lng });
  }, [emit, upsertMarker]);

  const runTextSearch = useCallback(() => {
    const q = (searchRef.current?.value || "").trim();
    setSearchError("");
    if (!q) return;
    const google = window.google;
    if (!google?.maps) { setSearchError("Map isn't ready yet — please wait a moment."); return; }
    setSearching(true);

    const tryPlaces = () => {
      const svc = placesSvcRef.current;
      if (!svc) {
        setSearching(false);
        setSearchError("Couldn't find that location. Add area/city, tap the map, or use GPS.");
        return;
      }
      svc.textSearch({ query: q, region: "in" }, (results, status) => {
        setSearching(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && results?.[0]?.geometry?.location) {
          const r = results[0];
          moveTo(r.geometry.location.lat(), r.geometry.location.lng(), r.formatted_address || r.name || q, extractPincode(r));
        } else {
          setSearchError("Couldn't find that location. Add area/city, tap the map, or use GPS.");
        }
      });
    };

    if (geocoderRef.current) {
      geocoderRef.current.geocode({ address: q, componentRestrictions: { country: "in" } }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          setSearching(false);
          const r = results[0];
          moveTo(r.geometry.location.lat(), r.geometry.location.lng(), r.formatted_address || q, extractPincode(r));
        } else {
          tryPlaces();
        }
      });
    } else {
      tryPlaces();
    }
  }, [moveTo]);

  // Boot the map exactly once. We purposefully use empty deps — all callbacks
  // referenced inside are stable (empty-deps useCallback or ref access).
  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsPlaces()
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const map = new google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        });
        mapInst.current = map;
        geocoderRef.current = new google.maps.Geocoder();
        if (google.maps.places?.PlacesService) {
          try { placesSvcRef.current = new google.maps.places.PlacesService(map); } catch { /* ignore */ }
        }

        map.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          upsertMarker(google, { lat, lng });
          map.panTo({ lat, lng });
          reverseAndEmit(lat, lng);
        });

        if (searchRef.current && google.maps.places) {
          const ac = new google.maps.places.Autocomplete(searchRef.current, {
            fields: ["geometry", "formatted_address", "address_components", "name"],
            componentRestrictions: { country: ["in"] },
          });
          ac.bindTo("bounds", map);
          ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place?.geometry?.location) return;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            map.panTo({ lat, lng });
            map.setZoom(PICKED_ZOOM);
            upsertMarker(google, { lat, lng });
            emit({
              address: place.formatted_address || place.name || "",
              pincode: extractPincode(place),
              lat,
              lng,
            });
          });
        }
        setMapReady(true);
      })
      .catch(() => setLoadError("Could not load Google Maps. Please refresh."));
    return () => {
      cancelled = true;
      try { markerRef.current?.setMap(null); } catch { /* ignore */ }
      markerRef.current = null;
      mapInst.current = null;
      geocoderRef.current = null;
      placesSvcRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyLocation = () => {
    setGpsError("");
    if (!navigator.geolocation) {
      setGpsError("Your browser does not support GPS.");
      return;
    }
    if (typeof window !== "undefined" && window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setGpsError("GPS needs a secure (https://) page. Please reload over https.");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const map = mapInst.current;
        if (map && window.google?.maps) {
          // Upsert the marker FIRST (self-heals a stale map/marker pair),
          // then pan + zoom. Order matters so the final visible state has
          // the fresh marker + correct viewport.
          upsertMarker(window.google, { lat, lng });
          map.panTo({ lat, lng });
          map.setZoom(PICKED_ZOOM);
        }
        reverseAndEmit(lat, lng);
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === 1) setGpsError("Permission denied. Enable location access for this site in your browser settings and try again.");
        else if (err.code === 2) setGpsError("Position unavailable. Check your device location setting and try again.");
        else if (err.code === 3) setGpsError("Timed out while locating. Please try again.");
        else setGpsError("Could not get your GPS location — please try again.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  return (
    <div className="space-y-2 mb-3" data-testid="booking-location-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
        <input
          ref={searchRef}
          type="text"
          placeholder="Search venue address, apartment or landmark"
          className="w-full pl-9 pr-20 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
          data-testid="booking-location-search"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runTextSearch(); } }}
        />
        <button
          type="button"
          onClick={runTextSearch}
          disabled={searching || !mapReady}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-semibold disabled:opacity-50"
          data-testid="booking-location-search-btn"
        >
          {searching ? <Loader2 size={12} className="animate-spin" /> : "Search"}
        </button>
      </div>

      {searchError && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="booking-location-search-error">
          <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-800">{searchError}</p>
        </div>
      )}

      <div className="relative rounded-xl overflow-hidden border border-gray-200" style={{ height: 220 }}>
        <div ref={mapRef} className="w-full h-full" />
        {!mapReady && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-xs text-[#64748b]">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading map…
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-rose-50 text-xs text-rose-700 px-4 text-center">
            {loadError}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={useMyLocation}
        disabled={gpsLoading || !mapReady}
        className="w-full flex items-center justify-center gap-2 bg-[#1E1B4B] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
        data-testid="booking-location-use-gps"
      >
        {gpsLoading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
        {gpsLoading ? "Getting GPS…" : "Use my current location"}
      </button>

      {gpsError && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="booking-location-gps-error">
          <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-800">{gpsError}</p>
        </div>
      )}

      {picked?.lat && picked?.lng ? (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="booking-location-captured">
          <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-emerald-700">Location pinned</p>
            <p className="text-[10px] text-[#64748b]">
              {picked.lat.toFixed(6)}, {picked.lng.toFixed(6)}
              {picked.pincode ? ` · PIN ${picked.pincode}` : ""}
            </p>
            <p className="text-[10px] text-[#64748b] mt-0.5">Edit the address below to add flat / floor / landmarks.</p>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-[#64748b] flex items-start gap-1">
          <MapPin size={11} className="text-[#E83E8C] mt-0.5 flex-shrink-0" />
          <span>Search, tap the map, or hit "Use my current location" to pin the exact venue.</span>
        </p>
      )}
    </div>
  );
};

export default BookingLocationPicker;
